import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Star,
  Clock,
  Users,
  BookOpen,
  TrendingUp,
  ChevronRight,
  Heart,
  ShoppingCart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useTenantContext } from '../../../context/TenantContext';
import { getAvailableCourses, type ElearningCourse } from '../../../services/elearningCourseService';

interface RecommendedCoursesProps {
  enrollments: any[];
}

// Fonction utilitaire pour formater le niveau de difficulté
const formatDifficultyLevel = (level: string) => {
  switch (level) {
    case 'beginner':
      return 'Débutant';
    case 'intermediate':
      return 'Intermédiaire';
    case 'advanced':
      return 'Avancé';
    default:
      return 'Inconnu';
  }
};

const RecommendedCourses: React.FC<RecommendedCoursesProps> = ({ enrollments }) => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const [recommendedCourses, setRecommendedCourses] = useState<ElearningCourse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadRecommendedCourses();
  }, [tenant?.id]);

  const loadRecommendedCourses = async () => {
    if (!tenant?.id) return;

    try {
      setIsLoading(true);
      const { courses } = await getAvailableCourses({ tenantId: tenant.id, limit: 6 });
      
      // Filtrer les cours non inscrits et prendre les 3 premiers
      const enrolledCourseIds = enrollments.map(e => e.course_id);
      const availableCourses = courses
        .filter(course => !enrolledCourseIds.includes(course.id))
        .slice(0, 3);
      
      setRecommendedCourses(availableCourses);
    } catch (error) {
      console.error('Erreur lors du chargement des cours recommandés:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewCourse = (courseSlug: string) => {
    navigate(`/elearning/courses/${courseSlug}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (isLoading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Star className="h-5 w-5 text-yellow-500" />
            <span>Recommandé pour vous</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex space-x-4">
                  <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (recommendedCourses.length === 0) {
    return (
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Star className="h-5 w-5 text-yellow-500" />
            <span>Recommandé pour vous</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              Aucun cours recommandé pour le moment
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center space-x-2">
          <Star className="h-5 w-5 text-yellow-500" />
          <span>Recommandé pour vous</span>
        </CardTitle>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/elearning/courses')}
          className="text-blue-500 hover:text-blue-600"
        >
          Voir tout
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          {recommendedCourses.map((course: ElearningCourse) => (
            <motion.div
              key={course.id}
              variants={cardVariants}
              className="group"
              onClick={() => handleViewCourse(course.slug)}
            >
              <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-900/30 dark:hover:to-purple-900/30 transition-all duration-200 cursor-pointer">
                
                {/* Course thumbnail */}
                <div className="relative w-full sm:w-20 h-20 flex-shrink-0">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center overflow-hidden mx-auto">
                    {course.thumbnail_url ? (
                      <img 
                        src={course.thumbnail_url} 
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <BookOpen className="h-8 w-8 text-white" />
                    )}
                  </div>
                  {/* New badge */}
                  <div className="absolute -top-2 -right-2">
                    <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs px-2 py-1">
                      Nouveau
                    </Badge>
                  </div>
                </div>

                {/* Course info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate pr-2">
                      {course.title}
                    </h3>
                    <div className="flex items-center space-x-1">
                      <Heart className="h-4 w-4 text-gray-400 hover:text-red-500 cursor-pointer transition-colors" />
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                    {course.description}
                  </p>

                  <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{course.duration_hours}h</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <TrendingUp className="h-4 w-4" />
                      <span>{formatDifficultyLevel(course.difficulty_level || 'beginner')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>{course.enrollment_count || 0} inscrits</span>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star}
                          className={`h-4 w-4 ${
                            star <= (course.rating_average || 4.5) 
                              ? 'text-yellow-400 fill-current' 
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {course.rating_average || 4.5} ({course.rating_count || 0} avis)
                    </span>
                  </div>
                </div>

                {/* Price and action */}
                <div className="flex flex-col items-end space-y-2">
                  <div className="text-right">
                    {course.price > 0 ? (
                      <div className="space-y-1">
                        <span className="text-lg font-bold text-green-600 dark:text-green-400">
                          {course.price} FCFA
                        </span>
                        {/* Pas de propriété discount dans l'interface, on peut la supprimer ou simuler */}
                      </div>
                    ) : (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Gratuit
                      </Badge>
                    )}
                  </div>
                  
                  <Button
                    size="sm"
                    className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewCourse(course.slug);
                    }}
                  >
                    <ShoppingCart className="h-4 w-4 mr-1" />
                    Voir
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* View all button */}
        <div className="mt-6 text-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/elearning/courses')}
            className="w-full"
          >
            Découvrir plus de cours
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecommendedCourses;
